import Creators from './Creators';
import React from 'react';
import {isEqual} from '@packages/utilities/memo';
import {Store} from './Store';
import {
  startDispatchTimer,
  stopDispatchTimer,
  startNotifySubscribersTimer,
  stopNotifySubscribersTimer,
  startStoreUpdatesTimer,
  stopStoreUpdatesTimer,
} from './FluxPerformanceUtil';
import {
  reportBug,
  type Metadata,
  type Params,
} from '@packages/utilities/reporter';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {TFlux, FluxAction, ChangeListener} from './types';
import {emptyObject} from '@packages/utilities/fp/utils';
import {NoLane, type Lane} from './Lanes';
import {
  type FluxDispatchMetrics,
  type FluxMetricsContext,
  createFluxMetricsContext,
  startFluxMetricsCollection,
  endFluxMetricsSyncPhase,
  getCurrentBatchAsyncMetrics,
  hasCurrentBatchAsyncIncrements,
  resetFluxMetricsCollection,
  setFluxMetric,
} from '@packages/systems/designer-telemetry';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

const alreadyLogged = {};

const now =
  typeof performance === 'object' ? () => performance.now() : Date.now;

export const FluxContext = React.createContext<TFlux<any, any, any>>(
  null as any
);

export const SyncFluxContext = React.createContext<boolean>(false);

type ExceptionReporter = (
  error: Error,
  metadata: Metadata,
  params?: Params
) => void;
type MetricReporter<StateType, SpecificActions> = (
  metric: string,
  value: number,
  fluxAction: FluxAction<StateType, SpecificActions>,
  metrics?: FluxDispatchMetrics,
  lane?: Lane
) => void;
type FatalExceptionReporter = (message: string) => void;
type ActionLogger<StateType, ActionType> = (
  state: StateType,
  action: ActionType
) => void;

type PendingMetricsReport<StateType, SpecificActions> = {
  actionType: string;
  dispatchRuntime: number;
  dispatchStart: number;
  fluxAction: FluxAction<StateType, SpecificActions>;
  metrics: FluxDispatchMetrics;
  lane: Lane;
};

// Currently trying to cap our flux calculation time
// at 16ms since UI still needs to update after this
// is done. 16ms ~60fps so this leaves the UI 16 ms
// to update to hit 30fps
const OVERLONG_DISPATCH_TIME_MS = 16;

export class Flux<
  StateType extends Record<string, any>,
  SpecificActions extends {type: string} = {
    type: string;
  },
  LegacyCreatorsType extends Record<string, any> = Record<string, any>,
> implements TFlux<StateType, SpecificActions, LegacyCreatorsType> {
  reportException?: ExceptionReporter;
  reportMetrics?: MetricReporter<StateType, SpecificActions>;
  reportFatalException?: FatalExceptionReporter;
  logFluxAction?: ActionLogger<StateType, SpecificActions>;
  reducer: (state: StateType, action: SpecificActions) => StateType;
  state: StateType;
  creators: LegacyCreatorsType;
  dispatcher: any;
  error: any;
  _store: Store<StateType>;
  private queue: Array<{action: SpecificActions; lane: Lane}> = [];
  private dispatching: boolean = false;
  private _metricsContext: FluxMetricsContext | undefined;
  private _registeredEngineCount: number = 0;
  private _pendingMetricsReports: Array<
    PendingMetricsReport<StateType, SpecificActions>
  > = [];
  private _metricsFlushScheduled: boolean = false;

  // @ts-expect-error - TS7006 - Parameter 'child' implicitly has an 'any' type. | TS7006 - Parameter 'onInitialRender' implicitly has an 'any' type.
  mount(child, {onInitialRender, sync} = emptyObject) {
    const element = React.createElement(FluxRoot, {
      // @ts-expect-error - TS2769 - No overload matches this call.
      flux: this,
      child,
      ref: this.handleRef,
      onInitialRender,
    });

    if (sync) {
      return React.createElement(
        SyncFluxContext.Provider,
        {value: true},
        element
      );
    }
    return element;
  }

  // @ts-expect-error - TS7006 - Parameter 'element' implicitly has an 'any' type.
  handleRef = (element) => {
    // @ts-expect-error - TS2339 - Property '__element' does not exist on type 'Flux'.
    this.__element = element;
  };

  constructor(
    reducer: (state: StateType, action: SpecificActions) => StateType,
    initialState: StateType,
    runtimeEnvironment: (typeof WebflowEnvironments)[keyof typeof WebflowEnvironments] = WebflowEnvironments.DESIGNER,
    options?: {
      reportException?: ExceptionReporter;
      reportMetrics?: MetricReporter<StateType, SpecificActions>;
      reportFatalException?: FatalExceptionReporter;
      logFluxAction?: ActionLogger<StateType, SpecificActions>;
    }
  ) {
    this.reportException = options?.reportException;
    this.reportMetrics = options?.reportMetrics;
    this.reportFatalException = options?.reportFatalException;
    this.logFluxAction = options?.logFluxAction;

    this.reducer = reducer;
    // @ts-expect-error - TS2339 - Property 'stores' does not exist on type 'Flux'.
    this.stores = {};
    this.creators = {} as LegacyCreatorsType;
    this.state = initialState as any;
    this.dispatching = false;
    this.dispatcher = {
      listeners: [],
      // @ts-expect-error - TS7006 - Parameter 'listener' implicitly has an 'any' type.
      register: (listener) => {
        this.dispatcher.listeners.push(listener);
        return listener;
      },
      // @ts-expect-error - TS7006 - Parameter 'listener' implicitly has an 'any' type.
      unregister: (listener) => {
        const {listeners} = this.dispatcher;
        const index = listeners.indexOf(listener);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      },
    };
    // @ts-expect-error - TS2339 - Property 'runtimeEnvironment' does not exist on type 'Flux'.
    this.runtimeEnvironment = runtimeEnvironment;

    // Fetch and cache the detailed metrics feature flag to avoid repeated lookups in dispatches.
    // If feature config isn't initialized, this defaults to false (detailed metrics disabled).
    // The presence of _metricsContext doubles as the "are metrics enabled?" guard.
    this._metricsContext = getFeatureFlagOutsideOfReact(
      FEATURE_FLAGS.FLUX_DETAILED_METRICS,
      {shouldLogExposure: false}
    )
      ? createFluxMetricsContext()
      : undefined;

    this._store = new Store(
      '@webflow/FluxRoot',
      initialState,
      this.getMetricsContext
    );

    Object.keys(initialState).forEach((name) => {
      const store = new Store(name, initialState[name], this.getMetricsContext);
      // @ts-expect-error - TS2339 - Property 'stores' does not exist on type 'Flux'.
      this.stores[name] = store;
    });
  }

  /**
   * Called by initEngines() to record engine count for metrics. The value is set once per
   * dispatch alongside other static metrics (e.g. totalRegisteredStores), not on every engine run.
   */
  setRegisteredEngineCountForMetrics(count: number): void {
    this._registeredEngineCount = count;
  }

  /**
   * Returns the metrics context if detailed metrics collection is enabled, or undefined.
   * Arrow function for a stable per-instance reference (safe to pass to weakMemo / closures).
   */
  getMetricsContext = (): FluxMetricsContext | undefined => {
    return this._metricsContext;
  };

  // @ts-expect-error - TS7006 - Parameter 'storeName' implicitly has an 'any' type.
  getStore(storeName) {
    // @ts-expect-error - TS2339 - Property 'stores' does not exist on type 'Flux'.
    const store = this.stores[storeName];

    if (!store) {
      // @ts-expect-error - TS2339 - Property 'stores' does not exist on type 'Flux'.
      const validStoreNames = Object.keys(this.stores).sort();
      const errorMessage = 'Unknown store name: ' + storeName;
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug('UnknownStoreName', errorMessage, {validStoreNames});
      if (process.env.NODE_ENV !== 'production') {
        throw new TypeError(errorMessage);
      }
    }

    return store;
  }

  // @ts-expect-error - TS7006 - Parameter 'descriptor' implicitly has an 'any' type.
  __DEPRECATED__registerCreators(descriptor) {
    if (this.creators[descriptor.name]) {
      console.error(
        new Error(`Creators already registered: ${descriptor.name}`)
      );
      return this.creators[descriptor.name];
    }

    const creators = new Creators(this, descriptor);
    // @ts-expect-error - TS2339 - Property 'creators' does not exist on type 'Flux'.
    this.creators[descriptor.name] = creators;

    if (!creators) {
      console.error(
        'Failed to register creators with descriptor: %o',
        descriptor
      );
    }

    return creators;
  }

  __DEPRECATED__getCreators: TFlux<
    StateType,
    SpecificActions,
    LegacyCreatorsType
  >['__DEPRECATED__getCreators'] = (legacyActionCreatorClassName) => {
    const creators = this.creators[legacyActionCreatorClassName];

    if (process.env.NODE_ENV !== 'production') {
      if (!creators) {
        const creatorNames = Object.keys(this.creators).sort();
        console.error(
          '`Flux.getCreators(className)` was called with "%o", ' +
            'but must be called with one of:\n  ',
          legacyActionCreatorClassName,
          creatorNames.join('\n  ')
        );
      }
    }

    return creators;
  };

  // @ts-expect-error - TS7006 - Parameter 'name' implicitly has an 'any' type.
  getStoreState = (name) => {
    return this.state[name];
  };

  getState = () => {
    return this.state;
  };

  onChange: ChangeListener<StateType> = (fn) => {
    return this._store.onChange(fn);
  };

  onChangeSync: ChangeListener<StateType> = (fn) => {
    return this._store.onChangeSync(fn);
  };

  dispatch = (action: SpecificActions, lane: Lane = NoLane) => {
    this.queue.push({action, lane});
    if (this.dispatching) return;
    this.dispatching = true;
    while (this.queue.length > 0) {
      const nextWork = this.queue.shift();
      if (nextWork) {
        this._dispatch(nextWork.action, nextWork.lane);
      }
    }
    this.dispatching = false;
  };

  private _dispatch = (action: SpecificActions, lane: Lane = NoLane) => {
    if (this.error) {
      console.warn(
        `Tried to dispatch ${action.type}, but Flux is sandboxed due to an internal error`
      );
      return;
    }

    if (action.hasOwnProperty('timestamp')) {
      throw new Error('The “timestamp” key is reserved inside actions.');
    }

    if (action.hasOwnProperty('state')) {
      throw new Error('The “state” key is reserved inside actions.');
    }

    if (process.env.NODE_ENV !== 'production') {
      startDispatchTimer(action);
    }

    const metricsCtx = this._metricsContext;
    const metricsForThisDispatch = metricsCtx
      ? startFluxMetricsCollection(metricsCtx)
      : undefined;

    const state = this.state;
    // @ts-expect-error - TS2339 - Property 'stores' does not exist on type 'Flux'.
    const stores = this.stores;
    const timestamp = Date.now();
    const dispatchStart = now();
    const fluxAction = {...action, timestamp, state};

    // @ts-expect-error - TS2339 - Property 'lastAction' does not exist on type 'Flux'.
    this.lastAction = fluxAction;

    let nextState;
    // keep track of which stores actually changed
    const storeChanges = [];

    if (process.env.NODE_ENV !== 'production') {
      startStoreUpdatesTimer(fluxAction);
    }

    try {
      nextState = this.reducer(state, fluxAction);
    } catch (error) {
      this.error = error;

      if (
        // @ts-expect-error - TS2339 - Property 'runtimeEnvironment' does not exist on type 'Flux'.
        this.runtimeEnvironment !== WebflowEnvironments.DESIGNER &&
        process.env.NODE_ENV !== 'test'
      ) {
        if (this.reportException) {
          this.reportException(
            error as Error,
            {from: 'Flux'},
            {severity: 'error'}
          );
        }
        if (this.reportFatalException) {
          this.reportFatalException('Store.js');
        }
      }

      // @ts-expect-error - TS2339 - Property '__element' does not exist on type 'Flux'. | TS2339 - Property '__element' does not exist on type 'Flux'.
      if (this.__element && typeof this.__element.setState === 'function') {
        // @ts-expect-error - TS2339 - Property '__element' does not exist on type 'Flux'.
        this.__element.setState({error, lastAction: fluxAction});
        return;
      } else {
        throw error;
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      stopStoreUpdatesTimer();
      startNotifySubscribersTimer();
    }

    this.state = nextState;

    // Count stores for metrics (each store has a reducer that was invoked)
    if (metricsCtx) {
      const storeKeys = Object.keys(stores);
      setFluxMetric(metricsCtx, 'totalRegisteredStores', storeKeys.length);
      // todo: storeReducersInvokedCount is currently always the same as totalRegisteredStores,
      //       as every store's reducer runs on every dispatch. The metric should be incremented
      //       as each reducer is actually called, but this is not technically possible until we
      //       refactor the reducer to be more selective.
      setFluxMetric(metricsCtx, 'storeReducersInvokedCount', storeKeys.length);
      setFluxMetric(
        metricsCtx,
        'totalRegisteredEngines',
        this._registeredEngineCount
      );
    }

    for (const key in stores) {
      const nextStoreState = nextState[key];
      stores[key].state = nextStoreState;
      // this store updated its state
      if (state[key] !== nextState[key]) {
        storeChanges.push(key);

        if (
          process.env.NODE_ENV === 'development' &&
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
          !alreadyLogged[key + action.type] &&
          isEqual(nextStoreState, state[key])
        ) {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
          alreadyLogged[key + action.type] = true;
          console.warn(
            `Wasteful update performed. The ${action.type} action handler` +
              ` in ${key} is going to cause identity checks to` +
              ` fail even though the state is deeply equal.`
          );
        }
      }
    }

    // Track stores that changed state
    if (metricsCtx) {
      setFluxMetric(metricsCtx, 'changedStoresCount', storeChanges.length);
    }

    this._store.state = nextState;

    for (const listener of this.dispatcher.listeners) {
      listener(fluxAction);
    }

    // only fire listeners for changed stores
    for (const key of storeChanges) {
      stores[key].emitChange(lane);
    }
    this._store.emitChange(lane);

    if (metricsCtx) {
      endFluxMetricsSyncPhase(metricsCtx);
    }

    if (process.env.NODE_ENV !== 'production') {
      stopNotifySubscribersTimer();
      stopDispatchTimer();
    }

    const dispatchRuntime = now() - dispatchStart;

    if (metricsCtx && metricsForThisDispatch) {
      this._pendingMetricsReports.push({
        actionType: action.type,
        dispatchRuntime,
        dispatchStart,
        fluxAction,
        metrics: metricsForThisDispatch,
        lane,
      });

      if (!this._metricsFlushScheduled) {
        this._metricsFlushScheduled = true;
        // Defer reporting until after async subscribers have been notified.
        this._store.onNextFlush(() => {
          const pendingReports = this._pendingMetricsReports;
          this._pendingMetricsReports = [];
          this._metricsFlushScheduled = false;

          const batchSize = pendingReports.length;
          const batchAsyncMetrics = getCurrentBatchAsyncMetrics(metricsCtx);
          const shouldMergeAsyncMetrics =
            hasCurrentBatchAsyncIncrements(metricsCtx);

          pendingReports.forEach((report, batchIndex) => {
            const totalDispatchDuration = now() - report.dispatchStart;
            report.metrics.totalDispatchDuration = totalDispatchDuration;
            report.metrics.batchSize = batchSize;
            report.metrics.batchIndex = batchIndex;

            if (batchAsyncMetrics && shouldMergeAsyncMetrics) {
              report.metrics.subscriberEffectCount =
                batchAsyncMetrics.subscriberEffectCount;
              report.metrics.selectorsInvokedCount =
                batchAsyncMetrics.selectorsInvokedCount;
              report.metrics.selectorsChangedCount =
                batchAsyncMetrics.selectorsChangedCount;
              report.metrics.enginesInvokedCount =
                batchAsyncMetrics.enginesInvokedCount;
              report.metrics.rootSubscriptionsInvokedCount =
                batchAsyncMetrics.rootSubscriptionsInvokedCount;
            }

            if (this.reportMetrics) {
              this.reportMetrics(
                report.actionType,
                report.dispatchRuntime,
                report.fluxAction,
                report.metrics,
                report.lane
              );
            }
          });

          resetFluxMetricsCollection(metricsCtx);
        });
      }
    } else {
      if (this.reportMetrics) {
        this.reportMetrics(
          action.type,
          dispatchRuntime,
          fluxAction,
          undefined,
          lane
        );
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      if (dispatchRuntime > OVERLONG_DISPATCH_TIME_MS) {
        console.warn(
          `${action.type} took ${dispatchRuntime.toFixed(3)}ms to dispatch`
        );
      }
    }

    if (this.logFluxAction) {
      this.logFluxAction(this.state, fluxAction);
    }
  };
}

class FluxRoot extends React.Component {
  state = {
    error: null,
    lastAction: null,
  };

  // @ts-expect-error - TS7006 - Parameter 'error' implicitly has an 'any' type.
  componentDidCatch(error) {
    // @ts-expect-error - TS2339 - Property 'flux' does not exist on type 'Readonly<{}>'.
    this.setState({error, lastAction: this.props.flux.lastAction});
  }

  componentDidMount() {
    // @ts-expect-error - TS2339 - Property 'onInitialRender' does not exist on type 'Readonly<{}>'.
    if (this.props.onInitialRender) {
      // @ts-expect-error - TS2339 - Property 'onInitialRender' does not exist on type 'Readonly<{}>'.
      this.props.onInitialRender();
    }
  }

  render() {
    return React.createElement(
      FluxContext.Provider,
      // @ts-expect-error - TS2339 - Property 'flux' does not exist on type 'Readonly<{}>'.
      {value: this.props.flux},
      // @ts-expect-error - TS2339 - Property 'child' does not exist on type 'Readonly<{}>'.
      React.createElement(this.props.child, this.state)
    );
  }
}
